import React, { useState } from "react";
import "@/App.css";
import AnypakuLogo from "@/components/Logo";
import HeroVan from "@/components/HeroVan";
import BookingWidget, { PHONE_RAW, PHONE_DISPLAY, WA_LINK } from "@/components/BookingWidget";
import MapSection from "@/components/MapSection";
import { PhoneIcon, WhatsAppIcon, VanIcon } from "@/components/Icons";
import { useI18n } from "@/contexts/I18nContext";
import AdminPanel from "@/pages/AdminPanel";
import { useConfig } from "@/hooks/useConfig";
import { Edit } from "@/contexts/EditModeContext";

/* ---------- LANGUAGE TOGGLE ---------- */
const LangToggle = () => {
  const { lang, setLang } = useI18n();
  return (
    <div
      className="inline-flex rounded-full p-1"
      style={{ background: "var(--navy-soft)" }}
      data-testid="lang-toggle"
    >
      {["fi", "en"].map((code) => (
        <button
          key={code}
          onClick={() => setLang(code)}
          data-testid={`lang-${code}`}
          className="px-3 py-1.5 rounded-full text-xs font-bold uppercase transition-all"
          style={{
            background: lang === code ? "var(--navy)" : "transparent",
            color: lang === code ? "#FFFFFF" : "var(--navy)",
            letterSpacing: "0.1em",
          }}
        >
          {code}
        </button>
      ))}
    </div>
  );
};

/* ---------- HEADER ---------- */
const Header = () => {
  const { t } = useI18n();
  return (
    <header
      className="sticky top-0 z-40 px-5 md:px-10 py-3 flex items-center justify-between"
      style={{
        background: "rgba(255, 255, 255, 0.92)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        borderBottom: "1px solid var(--line)",
      }}
      data-testid="site-header"
    >
      <a href="#top" className="flex items-center gap-3" data-testid="header-logo">
        <AnypakuLogo size={26} />
      </a>

      <nav
        className="hidden lg:flex items-center gap-7 text-sm"
        style={{ color: "var(--midnight)" }}
      >
        <a href="#services" data-testid="nav-services" className="hover:text-[var(--orange)] transition-colors font-medium">
          {t.nav.services}
        </a>
        <a href="#pricing" data-testid="nav-pricing" className="hover:text-[var(--orange)] transition-colors font-medium">
          {t.nav.pricing}
        </a>
        <a href="#contact" data-testid="nav-contact" className="hover:text-[var(--orange)] transition-colors font-medium">
          {t.nav.contact}
        </a>
      </nav>

      <div className="flex items-center gap-2">
        <LangToggle />
        <a
          href="#book"
          className="btn-orange hidden md:inline-flex"
          style={{ padding: "10px 20px", fontSize: 13 }}
          data-testid="header-book"
        >
          {t.nav.book} →
        </a>
      </div>
    </header>
  );
};

/* ---------- HERO ---------- */
const DEFAULT_TRUST_BADGES = [
  { icon: "★", num: "4.9/5", label: { fi: "240+ arviota", en: "240+ reviews" } },
  { icon: "⚡", num: "< 60 min", label: { fi: "vasteaika", en: "response time" } },
  { icon: "🛡", num: "5 000 €", label: { fi: "vakuutettu", en: "insured" } },
];

const Hero = () => {
  const { t, lang } = useI18n();
  const config = useConfig();
  const trustBadges =
    config.trustBadges && config.trustBadges.length > 0
      ? config.trustBadges
      : DEFAULT_TRUST_BADGES;
  return (
    <section
      id="top"
      className="relative px-5 md:px-10 pt-12 pb-20 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Decorative shapes */}
      <div
        className="absolute -top-40 -right-40 rounded-full opacity-30"
        style={{
          width: 600,
          height: 600,
          background: "radial-gradient(circle, var(--orange) 0%, transparent 70%)",
        }}
      />
      <div
        className="absolute -bottom-32 -left-32 rounded-full opacity-25"
        style={{
          width: 500,
          height: 500,
          background: "radial-gradient(circle, var(--navy) 0%, transparent 70%)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-center pt-6">
        <div className="lg:col-span-6 fade-up">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
            style={{
              background: "var(--orange-soft)",
              color: "var(--orange-2)",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              fontWeight: 700,
            }}
            data-testid="hero-eyebrow"
          >
            <Edit path="hero.eyebrow">{t.hero.eyebrow}</Edit>
          </div>

          <h1
            className="display"
            style={{
              fontSize: "clamp(40px, 11vw, 100px)",
              color: "var(--navy)",
              lineHeight: 0.92,
              wordBreak: "break-word",
              hyphens: "auto",
            }}
          >
            <Edit path="hero.title1">{t.hero.title1}</Edit>
            <br />
            <span style={{ color: "var(--orange)" }}>
              <Edit path="hero.title2">{t.hero.title2}</Edit>
            </span>
          </h1>

          <p
            className="mt-6 text-lg md:text-xl max-w-xl"
            style={{ color: "var(--midnight)", opacity: 0.78, lineHeight: 1.55 }}
          >
            <Edit path="hero.sub">{t.hero.sub}</Edit>{" "}
            <span
              style={{
                background: "var(--orange)",
                color: "#FFFFFF",
                padding: "2px 10px",
                borderRadius: 999,
                fontWeight: 800,
                fontFamily: "var(--font-display)",
                letterSpacing: 1,
              }}
            >
              {config.services.noLabour.base}&nbsp;€/h
            </span>
            .
          </p>

          <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
            <a
              href={`tel:${PHONE_RAW}`}
              className="btn-primary w-full sm:w-auto justify-center"
              data-testid="hero-call"
            >
              <span className="inline-flex items-center gap-2">
                <PhoneIcon size={16} /> {t.hero.ctaCall}
              </span>
              <span className="opacity-90">·</span>
              <span>{PHONE_DISPLAY}</span>
            </a>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noreferrer noopener"
              className="btn-orange w-full sm:w-auto justify-center"
              data-testid="hero-wa"
            >
              <span className="inline-flex items-center gap-2">
                <WhatsAppIcon size={18} /> {t.hero.ctaWA}
              </span>
            </a>
          </div>

          {/* Trust strip — mobile-friendly grid */}
          <div className="mt-8 grid grid-cols-3 gap-2 sm:flex sm:flex-wrap sm:gap-x-7 sm:gap-y-3 sm:items-center">
            {trustBadges.map((b, idx) => {
              const lbl =
                typeof b.label === "string"
                  ? b.label
                  : b.label?.[lang] || b.label?.fi || "";
              return (
                <div
                  key={`${b.num}-${idx}`}
                  className="flex flex-col sm:flex-row sm:items-center sm:gap-2 text-center sm:text-left"
                  style={{ color: "var(--midnight)" }}
                >
                  <span
                    className="text-2xl sm:text-base sm:font-extrabold"
                    style={{ color: "var(--orange)" }}
                  >
                    {b.icon}
                  </span>
                  <div className="flex flex-col sm:flex-row sm:gap-2 sm:items-baseline">
                    <span className="text-sm font-bold" style={{ color: "var(--navy)" }}>
                      {b.num}
                    </span>
                    <span className="text-xs sm:text-sm" style={{ opacity: 0.7 }}>
                      {lbl}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-6 flex items-center justify-center fade-up order-2 lg:order-2 -mt-2 lg:mt-0">
          <div className="float-slow">
            <HeroVan width={680} />
          </div>
        </div>
      </div>

      {/* Booking widget anchor */}
      <div
        id="book"
        className="relative max-w-[900px] mx-auto mt-8 md:mt-12 z-10"
      >
        <BookingWidget />
      </div>
    </section>
  );
};

/* ---------- SERVICE CARDS ---------- */
const ServiceCard = ({ id, accent = false, t, lang, basePrice }) => {
  const data = t.services[id];
  const priceLabel =
    basePrice !== undefined && basePrice !== null
      ? lang === "fi"
        ? `alk. ${Math.round(basePrice)} €/h`
        : `from €${Math.round(basePrice)}/h`
      : data.from;
  return (
    <div
      className="rounded-3xl p-7 md:p-8 transition-transform hover:-translate-y-2"
      style={{
        background: accent ? "var(--navy)" : "#FFFFFF",
        color: accent ? "#FFFFFF" : "var(--midnight)",
        border: accent ? "none" : "1px solid var(--line)",
        boxShadow: accent
          ? "0 30px 60px -20px rgba(26, 75, 140, 0.45)"
          : "0 6px 18px -8px rgba(15, 36, 64, 0.08)",
      }}
      data-testid={`service-card-${id}`}
    >
      <div
        className="display"
        style={{
          fontSize: 26,
          color: accent ? "var(--orange)" : "var(--orange)",
          marginBottom: 8,
        }}
      >
        {data.t}
      </div>
      <p
        style={{
          color: accent ? "rgba(255,255,255,0.8)" : "var(--mute)",
          lineHeight: 1.55,
          fontSize: 14,
        }}
      >
        {data.d}
      </p>

      <ul className="mt-5 space-y-2 text-sm">
        {[data.bullet1, data.bullet2, data.bullet3].map((b, i) => (
          <li key={i} className="flex items-start gap-2">
            <span style={{ color: "var(--orange)", fontWeight: 800 }}>✓</span>
            <span style={{ color: accent ? "rgba(255,255,255,0.85)" : "var(--midnight)" }}>{b}</span>
          </li>
        ))}
      </ul>

      <div
        className="mt-6 pt-5 flex items-baseline justify-between"
        style={{ borderTop: `1px solid ${accent ? "rgba(255,255,255,0.15)" : "var(--line)"}` }}
      >
        <span
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 800,
            fontSize: 28,
            color: accent ? "#FFFFFF" : "var(--navy)",
          }}
        >
          {priceLabel}
        </span>
        <a
          href="#book"
          className="text-sm font-bold"
          style={{
            color: accent ? "var(--orange)" : "var(--navy)",
          }}
        >
          → {accent ? "Book" : "→"}
        </a>
      </div>
    </div>
  );
};

const Services = () => {
  const { t, lang } = useI18n();
  const config = useConfig();
  return (
    <section
      id="services"
      className="px-5 md:px-10 py-24"
      style={{ background: "var(--cream)", borderTop: "1px solid var(--line)" }}
      data-testid="services-section"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-12 max-w-2xl">
          <div className="eyebrow mb-3" style={{ color: "var(--orange)" }}>
            ★ {t.nav.services}
          </div>
          <h2
            className="display"
            style={{
              fontSize: "clamp(40px, 5vw, 76px)",
              color: "var(--navy)",
            }}
          >
            <Edit path="services.heading">{t.services.heading}</Edit>
          </h2>
          <p
            className="mt-4 text-lg"
            style={{ color: "var(--mute)" }}
          >
            <Edit path="services.sub">{t.services.sub}</Edit>
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <ServiceCard id="noLabour" t={t} lang={lang} basePrice={config.services?.noLabour?.base} />
          <ServiceCard id="driverHelp" accent t={t} lang={lang} basePrice={config.services?.driverHelp?.base} />
          <ServiceCard id="twoMen" t={t} lang={lang} basePrice={config.services?.twoMen?.base} />
        </div>
      </div>
    </section>
  );
};

/* ---------- WHY US ---------- */
const Why = () => {
  const { t, lang } = useI18n();
  const config = useConfig();
  const icons = ["⚡", "💰", "🛡", "🌍"];
  return (
    <section
      id="pricing"
      className="px-5 md:px-10 py-24"
      data-testid="why-section"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-12 max-w-2xl">
          <div className="eyebrow mb-3" style={{ color: "var(--orange)" }}>
            ★ <Edit path="why.heading">{t.why.heading}</Edit>
          </div>
          <h2
            className="display"
            style={{
              fontSize: "clamp(40px, 5vw, 76px)",
              color: "var(--navy)",
            }}
          >
            <Edit path="why.sub">{t.why.sub}</Edit>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {(config.whyItems && config.whyItems.length > 0
            ? config.whyItems.map((it) => ({
                t: it.t?.[lang] || it.t?.fi || "",
                d: it.d?.[lang] || it.d?.fi || "",
              }))
            : t.why.items
          ).map((it, i) => (
            <div
              key={i}
              className="rounded-2xl p-6 transition-transform hover:-translate-y-1"
              style={{ background: "var(--cream)", border: "1px solid var(--line)" }}
              data-testid={`why-item-${i}`}
            >
              <div
                className="text-3xl mb-3"
                style={{ color: "var(--orange)" }}
              >
                {icons[i]}
              </div>
              <div
                className="display"
                style={{ color: "var(--navy)", fontSize: 22 }}
              >
                {it.t}
              </div>
              <p
                className="mt-2 text-sm"
                style={{ color: "var(--mute)", lineHeight: 1.55 }}
              >
                {it.d}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- FAQ ---------- */
const FAQ = () => {
  const { t, lang } = useI18n();
  const config = useConfig();
  const [openIdx, setOpenIdx] = useState(0);
  const items =
    config.faqItems && config.faqItems.length > 0
      ? config.faqItems.map((it) => ({
          q: it.q?.[lang] || it.q?.fi || "",
          a: it.a?.[lang] || it.a?.fi || "",
        }))
      : t.faq.items;
  return (
    <section
      className="px-5 md:px-10 py-24"
      style={{ background: "var(--cream)", borderTop: "1px solid var(--line)" }}
      data-testid="faq-section"
    >
      <div className="max-w-[900px] mx-auto">
        <div className="mb-10 text-center">
          <div className="eyebrow mb-3" style={{ color: "var(--orange)" }}>
            ★ FAQ
          </div>
          <h2
            className="display"
            style={{
              fontSize: "clamp(36px, 4vw, 64px)",
              color: "var(--navy)",
            }}
          >
            <Edit path="faq.heading">{t.faq.heading}</Edit>
          </h2>
        </div>

        <div className="space-y-3">
          {items.map((item, i) => {
            const open = openIdx === i;
            return (
              <button
                key={i}
                onClick={() => setOpenIdx(open ? -1 : i)}
                className="w-full text-left rounded-2xl p-5 transition-all"
                style={{
                  background: "#FFFFFF",
                  border: open ? "2px solid var(--orange)" : "1px solid var(--line)",
                }}
                data-testid={`faq-${i}`}
              >
                <div className="flex items-center justify-between gap-4">
                  <span
                    className="font-bold"
                    style={{
                      color: "var(--navy)",
                      fontSize: 16,
                      fontFamily: "var(--font-sans)",
                    }}
                  >
                    {item.q}
                  </span>
                  <span
                    style={{
                      color: "var(--orange)",
                      fontSize: 22,
                      fontWeight: 700,
                      transform: open ? "rotate(45deg)" : "rotate(0)",
                      transition: "transform 0.2s ease",
                    }}
                  >
                    +
                  </span>
                </div>
                {open && (
                  <p
                    className="mt-3 text-sm"
                    style={{
                      color: "var(--mute)",
                      lineHeight: 1.6,
                    }}
                  >
                    {item.a}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ---------- FOOTER ---------- */
const Footer = () => {
  const { t } = useI18n();
  return (
    <footer
      className="px-5 md:px-10 py-14"
      style={{
        background: "var(--navy)",
        color: "#FFFFFF",
      }}
      data-testid="site-footer"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-10">
        <div>
          <div style={{ filter: "brightness(0) invert(1)", display: "inline-block", marginBottom: 12 }}>
            {/* white logo via filter on the navy/orange logo */}
          </div>
          <div
            className="display"
            style={{ fontSize: 26, lineHeight: 1, color: "#FFFFFF" }}
          >
            ANYP<span style={{ color: "var(--orange)" }}>A</span>KU
          </div>
          <p
            className="mt-3 text-sm"
            style={{ opacity: 0.7, lineHeight: 1.55 }}
          >
            <Edit path="footer.tag">{t.footer.tag}</Edit>
          </p>
        </div>

        <div className="text-sm space-y-2">
          <div
            className="font-bold mb-2"
            style={{ fontFamily: "var(--font-display)", fontSize: 14, letterSpacing: "0.15em", textTransform: "uppercase" }}
          >
            {t.nav.contact}
          </div>
          <div style={{ opacity: 0.85 }}>{t.map.address}</div>
          <a
            href={`tel:${PHONE_RAW}`}
            className="block hover:underline"
            style={{ color: "var(--orange)" }}
            data-testid="footer-tel"
          >
            {PHONE_DISPLAY}
          </a>
          <a
            href="mailto:info@anypaky.fi"
            className="block hover:underline"
            style={{ opacity: 0.85 }}
            data-testid="footer-email"
          >
            info@anypaky.fi
          </a>
          <div style={{ opacity: 0.6, fontSize: 12, marginTop: 4 }}>
            {t.footer.hours}
          </div>
        </div>

        <div className="text-sm">
          <div
            className="font-bold mb-2"
            style={{ fontFamily: "var(--font-display)", fontSize: 14, letterSpacing: "0.15em", textTransform: "uppercase" }}
          >
            {t.nav.services}
          </div>
          <ul className="space-y-1.5" style={{ opacity: 0.8 }}>
            <li>{t.services.noLabour.t}</li>
            <li>{t.services.driverHelp.t}</li>
            <li>{t.services.twoMen.t}</li>
          </ul>
        </div>
      </div>

      <div
        className="max-w-[1400px] mx-auto mt-10 pt-6 flex flex-col md:flex-row gap-3 justify-between items-center"
        style={{ borderTop: "1px solid rgba(255,255,255,0.1)", fontSize: 12, opacity: 0.6 }}
      >
        <span><Edit path="footer.legal">{t.footer.legal}</Edit></span>
        <span style={{ fontFamily: "var(--font-mono)", letterSpacing: "0.15em", textTransform: "uppercase" }}>
          <Edit path="footer.madeWith">{t.footer.madeWith}</Edit>
        </span>
      </div>
    </footer>
  );
};

/* ---------- STICKY MOBILE ACTION BAR ---------- */
const MobileActionBar = () => {
  const { t } = useI18n();
  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 md:hidden"
      style={{
        background: "rgba(255,255,255,0.96)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        borderTop: "1px solid var(--line)",
        boxShadow: "0 -8px 24px rgba(15,36,64,0.10)",
        paddingBottom: "max(8px, env(safe-area-inset-bottom))",
      }}
      data-testid="mobile-action-bar"
    >
      <div className="grid grid-cols-3 gap-2 p-3">
        <a
          href={`tel:${PHONE_RAW}`}
          className="flex flex-col items-center justify-center gap-1 rounded-2xl py-2.5 transition-transform active:scale-95"
          style={{ background: "var(--navy)", color: "#FFFFFF" }}
          data-testid="mobile-bar-call"
        >
          <span style={{ display: "inline-flex" }}><PhoneIcon size={22} /></span>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.05em" }}>
            {t.hero.ctaCall}
          </span>
        </a>
        <a
          href={WA_LINK}
          target="_blank"
          rel="noreferrer noopener"
          className="flex flex-col items-center justify-center gap-1 rounded-2xl py-2.5 transition-transform active:scale-95"
          style={{ background: "#25D366", color: "#FFFFFF" }}
          data-testid="mobile-bar-wa"
        >
          <span style={{ display: "inline-flex" }}><WhatsAppIcon size={22} /></span>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.05em" }}>
            WhatsApp
          </span>
        </a>
        <a
          href="#book"
          className="flex flex-col items-center justify-center gap-1 rounded-2xl py-2.5 transition-transform active:scale-95"
          style={{ background: "var(--orange)", color: "#FFFFFF" }}
          data-testid="mobile-bar-book"
        >
          <span style={{ display: "inline-flex" }}><VanIcon size={22} /></span>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.05em" }}>
            {t.nav.book}
          </span>
        </a>
      </div>
    </div>
  );
};

/* ---------- APP ---------- */
const ColorOverride = () => {
  const config = useConfig();
  if (!config.colors) return null;
  const { navy, orange } = config.colors;
  // Only inject when at least one differs from default to keep CSS clean
  const css = `:root{${navy ? `--navy:${navy};` : ""}${orange ? `--orange:${orange};` : ""}}`;
  return <style>{css}</style>;
};

function App() {
  /* Simple path-based routing (no react-router needed) */
  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  if (path === "/admin" || path === "/admin/") {
    return <AdminPanel />;
  }

  return (
    <div className="App" data-testid="app-root" style={{ paddingBottom: "max(0px, env(safe-area-inset-bottom))" }}>
      <ColorOverride />
      <Header />
      <Hero />
      <Services />
      <Why />
      <MapSection />
      <FAQ />
      <Footer />
      <MobileActionBar />
      {/* Spacer below footer so sticky bar doesn't overlap content on mobile */}
      <div className="md:hidden" style={{ height: 88 }} aria-hidden />
    </div>
  );
}

export default App;
